import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, EffectFade } from "swiper/modules";
import AppContext from "../../../AppContext";
import useTendering from "../../../hooks/useTendering";
import useUsers from "../../../hooks/useUsers";
import useCompanies from "../../../hooks/useCompanies";
import { useNavigate } from "react-router-dom";
import { formatNumber } from "../../../helpers/common.helper";
import StylishButton from "../../../components/common/StylishButton";
import { ReactComponent as PlaySvg } from "../../../assets/icons/play.svg";

const assetsUri = import.meta.env.ASSETS_URI;

const Urgent = (props) => {
  const { designStyles } = useContext(AppContext);
  const { query } = useTendering();
  const { queryUsers } = useUsers();
  const { queryCompany } = useCompanies();
  const [datasource, setDatasource] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(undefined);
  const [nextSlideIndex, setNextSlideIndex] = useState(undefined);
  const navigate = useNavigate();
  const coverSwiperRef = useRef();
  const detailSwiperRef = useRef();

  const fetchData = async () => {
    const { _metadata, data } = await query(
      { active: true, deleted: false },
      1,
      3,
      null,
      { createdOn: -1 }
    );

    // * Get owner
    const userIds = data.map((u) => u.createdBy);
    const companyIds = data.map((u) => u.company).map((d) => !!d);

    const [userResults, companyResults] = await Promise.allSettled([
      queryUsers({ _id: { $in: userIds } }, null, -1, {
        _id: 1,
        firstname: 1,
        surname: 1,
        avatar: 1,
        uri: 1,
      }),
      queryCompany({ _id: { $in: userIds } }, null, -1, {
        _id: 1,
        logo: 1,
        name: 1,
      }),
    ]);

    setDatasource(
      data.map((d) => {
        // * Map user or company
        const _user = userResults.value.data.find(
          (user) => user._id === d.createdBy
        );

        // TODO: Map for company

        d.createdBy = _user;

        return d;
      })
    );

    setCurrentIndex(0);
    setNextSlideIndex(1);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    coverSwiperRef.current.swiper.slideTo(currentIndex);
    detailSwiperRef.current.swiper.slideTo(currentIndex);
  }, [currentIndex]);

  return (
    <div className="tendering__landing__urgent">
      <h2 className="tendering__landing__urgent__title">Urgent Works!</h2>
      <div
        className="tendering__landing__urgent__slides"
        style={{
          gridTemplateColumns: `repeat(${datasource?.length}, minmax(0, 1fr))`,
        }}
      >
        {datasource?.map((_, index) => (
          <div
            className={`tendering__landing__urgent__slides__tab ${
              currentIndex === index
                ? "tendering__landing__urgent__slides__tab--active"
                : ""
            }`}
            onClick={() => {
              if (currentIndex === index) return;

              setCurrentIndex(index);
            }}
          >
            <div className="tendering__landing__urgent__slides__tab__line" />
            {currentIndex === index ? `0${index + 1}` : <></>}
          </div>
        ))}
      </div>
      <div className="tendering__landing__urgent__left">
        <Swiper
          ref={coverSwiperRef}
          className="tendering__landing__urgent__covers"
          effect={"fade"}
          modules={[EffectFade]}
          slidesPerView={1}
          onActiveIndexChange={(swiper) => {
            setCurrentIndex(swiper.activeIndex);

            if (swiper.isEnd) {
              setNextSlideIndex(0);
              return;
            }
            setNextSlideIndex(swiper.activeIndex + 1);
          }}
          rewind={true}
        >
          {datasource?.map((data, index) => (
            <SwiperSlide key={index}>
              <div className="tendering__landing__urgent__covers__slide">
                <img
                  className="tendering__landing__urgent__covers__slide__image"
                  src={`${assetsUri}/${
                    data.images.cover.cropped ?? data.images.cover.original
                  }`}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="tendering__landing__urgent__right">
        <Swiper
          ref={detailSwiperRef}
          className="tendering__landing__urgent__details"
          effect={"fade"}
          modules={[EffectFade]}
          slidesPerView={1}
          onActiveIndexChange={(swiper) => {
            setCurrentIndex(swiper.activeIndex);

            if (swiper.isEnd) {
              setNextSlideIndex(0);
              return;
            }
            setNextSlideIndex(swiper.activeIndex + 1);
          }}
          rewind={true}
        >
          {datasource?.map((data, index) => (
            <SwiperSlide key={index}>
              <div className="tendering__landing__urgent__details__slide">
                <div className="tendering__landing__urgent__details__slide__previews">
                  {[...data.images.gallery.slice(0, 2)].map((image) => (
                    <img
                      className="tendering__landing__urgent__details__slide__previews__image"
                      src={`${assetsUri}/${image.cropped ?? image.original}`}
                    />
                  ))}
                </div>
                <div className="tendering__landing__urgent__details__slide__info">
                  <span className="tendering__landing__urgent__details__slide__info__name">
                    Scope of Work
                  </span>
                  <span className="tendering__landing__urgent__details__slide__info__value">
                    {
                      designStyles.works.find(
                        (w) => w.value === datasource[currentIndex].work
                      )?.name
                    }
                  </span>
                  <span className="tendering__landing__urgent__details__slide__info__name">
                    Type of Space
                  </span>
                  <span className="tendering__landing__urgent__details__slide__info__value">
                    {
                      designStyles.spaces.find(
                        (w) => w.value === datasource[currentIndex].space
                      )?.name
                    }
                  </span>
                  <span className="tendering__landing__urgent__details__slide__info__name">
                    Styles
                  </span>
                  <span className="tendering__landing__urgent__details__slide__info__value">
                    {datasource[currentIndex].styles.map((style) => (
                      <span className="tendering__landing__urgent__details__slide__info__value__tag">
                        {
                          designStyles.styles.find((w) => w.value === style)
                            ?.name
                        }
                      </span>
                    ))}
                  </span>
                  <span className="tendering__landing__urgent__details__slide__info__name">
                    Location
                  </span>
                  <span className="tendering__landing__urgent__details__slide__info__value">
                    {datasource[currentIndex].location}
                  </span>
                  <span className="tendering__landing__urgent__details__slide__info__name">
                    Size
                  </span>
                  <span className="tendering__landing__urgent__details__slide__info__value">
                    250 sq.m.
                  </span>
                  {datasource[currentIndex].budget && (
                    <>
                      <span className="tendering__landing__urgent__details__slide__info__name">
                        Budget
                      </span>
                      <span className="tendering__landing__urgent__details__slide__info__value">
                        {formatNumber(datasource[currentIndex].budget, "THB")}
                      </span>
                    </>
                  )}
                </div>
                <div style={{ flexGrow: 1 }} />
                <div className="tendering__landing__urgent__details__slide__view">
                  <StylishButton
                    label={"View now"}
                    icon={<PlaySvg />}
                    onClick={() => navigate(data._id)}
                    size="small"
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

Urgent.propTypes = {};

export default Urgent;
